import { ClarityUnmaskDirective } from 'app/shared/directives/clarity-unmask.directive';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiErrorCode } from '@interfaces/httpResponses';
import { AuthService } from '@service-features/auth.service';
import { ApiErrorAdapter } from '@shared-services/handlers/api-error-adapter.service';
import { NotificationService } from '@shared-services/handlers/notification.service';
import { firstValueFrom } from 'rxjs';
import { InlineSpinnerComponent } from '@shared-components/inline-spinner/inline-spinner.component';

export enum ConfirmationViewState {
  MISSING = 'missing',
  LOADING = 'loading',
  SUCCESS = 'success',
  INVALID = 'invalid',
  EXPIRED = 'expired',
  USED = 'used',
  ERROR = 'error',
}

@Component({
  selector: 'gd-email-confirmation',
  standalone: true,
  imports: [
    InlineSpinnerComponent,
    ClarityUnmaskDirective,
    CommonModule,
    FormsModule,
    RouterLink,
  ],
  templateUrl: './email-confirmation.component.html',
  styleUrl: './email-confirmation.component.scss',
})
export class EmailConfirmationComponent implements OnInit {
  readonly states = ConfirmationViewState;
  state = ConfirmationViewState.LOADING;
  email = '';
  resending = false;
  get canResend(): boolean {
    return [
      ConfirmationViewState.INVALID,
      ConfirmationViewState.EXPIRED,
      ConfirmationViewState.USED,
      ConfirmationViewState.MISSING,
    ].includes(this.state);
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private errors: ApiErrorAdapter,
    private notifications: NotificationService,
  ) {}

  ngOnInit(): void {
    void this.confirm();
  }
  async resend(): Promise<void> {
    if (!this.email.trim() || this.resending) return;
    this.resending = true;
    try {
      const response = await firstValueFrom(
        this.auth.resendEmailConfirmation(this.email),
      );
      this.notifications.showTotast(response.payload);
    } catch (error: unknown) {
      this.notifications.showToastError(
        error,
        'No se pudo reenviar el email de confirmación.',
      );
    } finally {
      this.resending = false;
    }
  }
  private async confirm(): Promise<void> {
    const token = this.route.snapshot.queryParamMap.get('token');
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true,
    });
    if (!token) {
      this.state = ConfirmationViewState.MISSING;
      return;
    }
    try {
      await firstValueFrom(this.auth.confirmEmail(token));
      this.state = ConfirmationViewState.SUCCESS;
    } catch (error: unknown) {
      const code = this.errors.from(error).code;
      this.state =
        code === ApiErrorCode.EMAIL_CONFIRMATION_TOKEN_EXPIRED
          ? ConfirmationViewState.EXPIRED
          : code === ApiErrorCode.EMAIL_CONFIRMATION_TOKEN_ALREADY_USED
            ? ConfirmationViewState.USED
            : code === ApiErrorCode.EMAIL_CONFIRMATION_TOKEN_INVALID
              ? ConfirmationViewState.INVALID
              : ConfirmationViewState.ERROR;
    }
  }
}
